/**
 * useInfiniteScroll - 無限滾動 Composable (整合優化版本)
 *
 * 統一的 API 設計，透過 manual 選項切換模式
 *
 * @param {Object|Function} options - 配置選項或 callback 函數（向下相容）
 *   通用選項：
 *     @param {Function} options.loadMore - 載入更多資料的函數，應返回 Promise 或 { complete: boolean }
 *     @param {boolean} [options.manual=false] - 是否手動控制生命週期
 *     @param {number} [options.throttleDelay=200] - 節流延遲時間 (毫秒)
 *     @param {Function} [options.onError] - 錯誤處理回調函數
 *
 *   自動模式選項 (manual=false)：
 *     @param {Ref<boolean>} options.isLastPage - 是否已到最後一頁的響應式引用
 *     @param {Ref<boolean>} options.isLoadingMore - 是否正在載入的響應式引用
 *     @param {number} [options.threshold=0.7] - 觸發載入的閾值 (0-1)
 *
 *   手動模式選項 (manual=true)：
 *     @param {HTMLElement|Ref|string} options.target - 目標滾動容器
 *     @param {number} [options.distance=100] - 距離底部多少px時觸發
 *
 * @param {Object} legacyOptions - 向下相容：第二個參數（當第一個參數是 Function 時）
 *
 * @returns {Object} 回傳物件
 *   自動模式 (manual=false)：
 *     @returns {Ref<HTMLElement|null>} scrollContainer - 滾動容器的 ref
 *     @returns {Function} cleanup - 清理函數
 *
 *   手動模式 (manual=true)：
 *     @returns {Function} init - 初始化監聽器
 *     @returns {Function} destroy - 銷毀監聽器
 *     @returns {Function} reset - 重置狀態
 *     @returns {Ref<boolean>} isLoading - 載入狀態
 *     @returns {Ref<boolean>} isComplete - 是否完成
 */

import throttle from 'lodash/throttle'
import { ref, onMounted, onBeforeUnmount, watch, nextTick, unref } from 'vue'

export function useInfiniteScroll(options = {}, legacyOptions = {}) {
  // 向下相容：支援舊的 callback 模式
  const isLegacyMode = typeof options === 'function'

  if (isLegacyMode) {
    // 舊的手動模式：callback 函數作為第一個參數
    return createManualMode({
      loadMore: options,
      manual: true,
      useLegacyCallback: true,
      ...legacyOptions,
    })
  }

  // 新的統一模式：根據 manual 選項決定
  const isManualMode = options.manual === true

  if (isManualMode) {
    return createManualMode(options)
  } else {
    return createAutoMode(options)
  }
}

/**
 * 手動模式：需要手動調用 init() 和 destroy()
 * 支援 throttle、更好的效能優化
 */
function createManualMode(options = {}) {
  const {
    loadMore,
    target = null,
    distance = 100,
    throttleDelay = 200,
    onError = (error) => console.error('Failed to load more:', error),
    useLegacyCallback = false, // 向下相容標記
  } = options

  // 驗證必要參數
  if (typeof loadMore !== 'function') {
    throw new Error('useInfiniteScroll: loadMore function is required')
  }

  const isLoading = ref(false)
  const isComplete = ref(false)

  let targetElement = null
  let throttledScrollHandler = null
  let useWindow = true

  // 向下相容：提供給舊 callback 使用的控制器
  const controller = {
    setLoading(val) {
      isLoading.value = !!val
    },
    setComplete(val = true) {
      isComplete.value = !!val
      if (val) isLoading.value = false
    },
  }

  const checkScroll = async () => {
    if (isLoading.value || isComplete.value) return
    if (!useWindow && !targetElement) return

    const scrollTop = useWindow ? window.pageYOffset || document.documentElement.scrollTop : targetElement.scrollTop

    const scrollHeight = useWindow ? document.documentElement.scrollHeight : targetElement.scrollHeight

    const clientHeight = useWindow ? window.innerHeight : targetElement.clientHeight

    // 接近底部時觸發
    if (scrollTop + clientHeight >= scrollHeight - distance) {
      isLoading.value = true
      try {
        if (useLegacyCallback) {
          // 向下相容：使用舊的 controller 模式
          await loadMore(controller)
          // 成功與否由 callback 決定何時關閉 loading 或標記完成
          if (isLoading.value && !isComplete.value) {
            isLoading.value = false
          }
        } else {
          // 新模式：支援返回值
          const result = await loadMore()
          if (result && result.complete) {
            isComplete.value = true
            isLoading.value = false
          } else {
            isLoading.value = false
          }
        }
      } catch (error) {
        onError(error)
        isLoading.value = false
      }
    }
  }

  // 使用 throttle 優化效能
  throttledScrollHandler = throttle(checkScroll, throttleDelay, {
    leading: true,
    trailing: true,
  })

  const init = () => {
    // 重置狀態
    isComplete.value = false

    // 取得目標元素
    const targetValue = target ? unref(target) : null

    // 判斷使用 window 還是指定容器
    if (targetValue) {
      if (typeof targetValue === 'string') {
        targetElement = document.querySelector(targetValue)
      } else {
        targetElement = targetValue
      }

      if (targetElement) {
        useWindow = false
        targetElement.addEventListener('scroll', throttledScrollHandler, { passive: true })
      } else {
        console.warn('Infinite scroll target element not found, fallback to window')
        useWindow = true
        window.addEventListener('scroll', throttledScrollHandler, { passive: true })
      }
    } else {
      useWindow = true
      window.addEventListener('scroll', throttledScrollHandler, { passive: true })
    }

    // 初始檢查（避免列表太短不觸發）
    queueMicrotask(() => checkScroll())
  }

  const destroy = () => {
    try {
      if (throttledScrollHandler) {
        throttledScrollHandler.cancel() // 取消待執行的 throttle
      }

      if (useWindow) {
        window.removeEventListener('scroll', throttledScrollHandler)
      } else if (targetElement) {
        targetElement.removeEventListener('scroll', throttledScrollHandler)
      }
    } finally {
      targetElement = null
    }
  }

  const reset = () => {
    isLoading.value = false
    isComplete.value = false
  }

  return {
    init,
    destroy,
    reset,
    isLoading,
    isComplete,
  }
}

/**
 * 自動模式：自動管理生命週期
 * 支援橫向/直向滾動、媒體查詢監聽
 */
function createAutoMode(options = {}) {
  const {
    loadMore,
    isLastPage,
    isLoadingMore,
    threshold = 0.7,
    throttleDelay = 200,
    onError = (error) => console.error('Failed to load more:', error),
  } = options

  // 驗證必要參數
  if (typeof loadMore !== 'function') {
    throw new Error('useInfiniteScroll: loadMore function is required')
  }

  const scrollContainer = ref(null)

  // 用於防止 throttle trailing 在 Vue 響應式更新延遲期間重複觸發
  let isRequestPending = false

  // 使用常數定義方向判斷邏輯，避免在每次滾動時重複查詢
  // SSR 安全檢查 + matchMedia 存在檢查
  const ORIENTATION_QUERIES =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? {
          portrait: window.matchMedia('(orientation: portrait)'),
          desktop: window.matchMedia('(min-width: 1024px)'),
        }
      : null

  // 快取方向狀態，避免重複查詢
  // 預設為橫向滾動
  let isVerticalScroll = ORIENTATION_QUERIES ? ORIENTATION_QUERIES.portrait.matches || ORIENTATION_QUERIES.desktop.matches : false

  // 監聽媒體查詢變化，更新快取狀態
  const updateOrientation = () => {
    isVerticalScroll = ORIENTATION_QUERIES.portrait.matches || ORIENTATION_QUERIES.desktop.matches
  }

  // 處理滾動事件的核心邏輯
  const handleScroll = (e) => {
    // 使用統一的載入狀態 + 同步請求鎖 + 初始載入標記
    if (
      !e.target || // 確保有目標元素
      isLoadingMore?.value || // 正在載入
      isLastPage?.value || // 已到最後一頁
      isRequestPending // 同步請求鎖（立即生效）
    ) {
      return
    }

    try {
      // 根據方向選擇對應的尺寸屬性
      const outerSize = isVerticalScroll ? e.target.clientHeight : e.target.clientWidth
      const innerSize = isVerticalScroll ? e.target.scrollHeight : e.target.scrollWidth
      const position = isVerticalScroll ? e.target.scrollTop : e.target.scrollLeft

      // 計算滾動百分比
      const scrollableDistance = innerSize - outerSize

      // 防止除以零
      if (scrollableDistance <= 0) {
        return
      }

      const scrollPercentage = position / scrollableDistance

      // 當滾動超過閾值時觸發載入
      if (scrollPercentage >= threshold) {
        isRequestPending = true

        loadMore()
          .catch(onError)
          .finally(() => {
            nextTick(() => {
              isRequestPending = false
            })
          })
      }
    } catch (error) {
      isRequestPending = false
    }
  }

  // 使用 throttle 包裝處理函數，限制執行頻率
  const throttledHandleScroll = throttle(handleScroll, throttleDelay, {
    leading: true, // 第一次呼叫立即執行
    trailing: true, // 確保最後一次也會執行
  })

  // 註冊媒體查詢監聽器
  const registerMediaListeners = () => {
    if (!ORIENTATION_QUERIES) return
    ORIENTATION_QUERIES.portrait.addEventListener('change', updateOrientation)
    ORIENTATION_QUERIES.desktop.addEventListener('change', updateOrientation)
  }

  // 移除媒體查詢監聽器
  const unregisterMediaListeners = () => {
    if (!ORIENTATION_QUERIES) return
    ORIENTATION_QUERIES.portrait.removeEventListener('change', updateOrientation)
    ORIENTATION_QUERIES.desktop.removeEventListener('change', updateOrientation)
  }

  // 清理函數
  const cleanup = () => {
    if (scrollContainer.value) {
      scrollContainer.value.removeEventListener('scroll', throttledHandleScroll)
    }
    throttledHandleScroll.cancel() // 取消待執行的 throttle
    unregisterMediaListeners()
  }

  // 組件掛載時設置
  onMounted(() => {
    registerMediaListeners()

    // 使用 nextTick 確保 DOM 已更新
    nextTick(() => {
      if (scrollContainer.value) {
        scrollContainer.value.addEventListener('scroll', throttledHandleScroll, { passive: true })
      } else {
        console.warn('useInfiniteScroll: scrollContainer ref is not attached to any element')
      }
    })
  })

  // 組件卸載前清理
  onBeforeUnmount(() => {
    cleanup()
  })

  // 監聽 scrollContainer 變化，重新註冊事件（用於動態切換滾動容器的場景）
  watch(scrollContainer, (newContainer, oldContainer) => {
    if (oldContainer) {
      oldContainer.removeEventListener('scroll', throttledHandleScroll)
    }
    if (newContainer) {
      newContainer.addEventListener('scroll', throttledHandleScroll, { passive: true })
    }
  })

  return {
    scrollContainer, // 綁定到滾動容器的 ref
    cleanup, // 手動清理函數（通常不需要，僅供特殊情況使用）
  }
}
